import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format, parse } from "date-fns";
import { it } from "date-fns/locale";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlusIcon as Plus, TrashIcon as Trash, LinkSimpleIcon as LinkSimple, CalculatorIcon as Calculator, ClockCounterClockwiseIcon as ClockCounterClockwise, CaretRightIcon as CaretRight, CaretDownIcon as CaretDown, BuildingsIcon as Buildings, FilePlusIcon as FilePlus, MagnifyingGlassIcon as MagnifyingGlass, PrinterIcon as Printer, CalendarBlankIcon as CalendarBlank, WarningCircleIcon as WarningCircle, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import { CopyCostStatements } from "@/components/CopyCostStatements";
import { ClientCombobox } from "@/components/ClientCombobox";
import { StatusBadge } from "@/components/common";
import { toast } from "sonner";

const TIP_LABEL = {
  solo_costo_copia: "Solo costo copia", canone_costo_copia: "Canone + costo copia",
  canone_incluse_eccedenze: "Canone + incluse + eccedenze",
};
// stati "validi" per l'associazione di una fotocopiatrice a un contratto esistente
const VALID_ASSOC_STATI = ["bozza", "attivo", "sospeso"];
const err = (e) => formatApiErrorDetail(e?.response?.data?.detail);

function DateField({ value, onChange, placeholder = "GG/MM/AAAA", testid }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const valid = parsed && !isNaN(parsed);
  // Allinea il testo visibile al valore esterno (anche quando scelto dal calendario)
  useEffect(() => { setText(valid ? format(parsed, "dd/MM/yyyy", { locale: it }) : ""); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = (raw) => {
    const s = (raw || "").trim();
    if (!s) { onChange(""); return; }
    const d = parse(s, "dd/MM/yyyy", new Date());
    if (!isNaN(d)) onChange(format(d, "yyyy-MM-dd"));
    else setText(valid ? format(parsed, "dd/MM/yyyy", { locale: it }) : ""); // ripristina se non valido
  };
  return (
    <div className="relative">
      <Input value={text} onChange={(e) => setText(e.target.value)} onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { commit(e.target.value); e.preventDefault(); } }}
        placeholder={placeholder} data-testid={testid} className="rounded-sm pr-9" />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testid ? `${testid}-calendar-btn` : undefined}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <CalendarBlank size={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar mode="single" locale={it} selected={valid ? parsed : undefined}
            onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

const EMPTY_CONTRACT = {
  cliente_id: "", titolo: "", stato: "attivo", data_inizio: "", data_scadenza: "",
  periodicita: "mensile", period_mode: "calendario", note: "",
  canone_generale: { attivo: false, importo: 0, descrizione: "", partial: "prorata_giorni" },
  partial_canone_generale: "prorata_giorni", partial_canone_device: "prorata_giorni",
  partial_included: "prorata_giorni",
};

export function CopyCostContracts({ presetDeviceId = null, presetClientId = null, autoNew = false, onDone }) {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [coverage, setCoverage] = useState({ per_client: {} });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CONTRACT);
  const [detailId, setDetailId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [autoAddDevice, setAutoAddDevice] = useState(false);
  // deep-link: scelta nuovo vs esistente
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [existingSel, setExistingSel] = useState("");
  // ricerca / filtro / raggruppamento
  const [q, setQ] = useState("");
  const [statoF, setStatoF] = useState("all");
  const [openClients, setOpenClients] = useState(() => new Set());
  const deepLinkHandled = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: cl }, cov] = await Promise.all([
        api.get("/copy-cost-contracts"), api.get("/clients"),
        api.get("/copy-cost-contracts/device-coverage").catch(() => ({ data: { per_client: {} } })),
      ]);
      setContracts(c || []);
      setClients((cl?.items || cl || []).map((x) => ({ id: x.id, nome: x.ragione_sociale })));
      setCoverage((cov && cov.data) || { per_client: {} });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // deep-link dal dispositivo: se c'è un device -> mostra la scelta; altrimenti crea nuovo
  useEffect(() => {
    if (!autoNew || deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    if (presetDeviceId) { setExistingSel(""); setChoiceOpen(true); }
    else { setForm({ ...EMPTY_CONTRACT, cliente_id: presetClientId || "" }); setCreateOpen(true); }
  }, [autoNew, presetDeviceId, presetClientId]);

  const save = async () => {
    if (!form.cliente_id) { toast.error("Seleziona un cliente"); return; }
    try {
      if (editId) {
        await api.put(`/copy-cost-contracts/${editId}`, form);
        toast.success("Contratto aggiornato");
        setCreateOpen(false); setEditId(null); await load(); onDone?.();
      } else {
        const { data } = await api.post("/copy-cost-contracts", form);
        toast.success("Contratto costo copia creato");
        setCreateOpen(false); await load(); onDone?.();
        setDetailId(data.id);
        if (presetDeviceId) setAutoAddDevice(true);
      }
    } catch (e) { toast.error(err(e)); }
  };

  // contratti esistenti validi per il cliente del deep-link
  const existingForClient = useMemo(
    () => contracts.filter((c) => c.cliente_id === presetClientId && VALID_ASSOC_STATI.includes(c.stato)),
    [contracts, presetClientId]);

  const chooseExisting = () => {
    if (!existingSel) { toast.error("Seleziona un contratto esistente"); return; }
    setChoiceOpen(false);
    setDetailId(existingSel);
    setAutoAddDevice(true);
  };
  const chooseNew = () => {
    setChoiceOpen(false);
    setForm({ ...EMPTY_CONTRACT, cliente_id: presetClientId || "" });
    setCreateOpen(true);
  };

  const [rinnovoOnly, setRinnovoOnly] = useState(false);
  const rinnovoCount = useMemo(() => contracts.filter((c) => ["da_rinnovare", "scaduto"].includes(c.stato)).length, [contracts]);

  // ---- raggruppamento per cliente (rispetta ricerca + filtro stato) ----
  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const rows = contracts.filter((c) => {
      if (rinnovoOnly && !["da_rinnovare", "scaduto"].includes(c.stato)) return false;
      if (statoF !== "all" && c.stato !== statoF) return false;
      if (!term) return true;
      return [c.numero, c.titolo, c.cliente_nome].some((v) => String(v || "").toLowerCase().includes(term));
    });
    const map = {};
    for (const c of rows) {
      const k = c.cliente_id || "__none__";
      if (!map[k]) map[k] = { cliente_id: k, cliente_nome: c.cliente_nome || "—", contracts: [], n_dev: 0 };
      map[k].contracts.push(c);
      map[k].n_dev += c.n_dispositivi || 0;
    }
    return Object.values(map).sort((a, b) => a.cliente_nome.localeCompare(b.cliente_nome));
  }, [contracts, q, statoF, rinnovoOnly]);

  // se ricerca/filtro lascia un solo cliente, espandilo automaticamente
  useEffect(() => {
    if (groups.length === 1) setOpenClients(new Set([groups[0].cliente_id]));
  }, [groups]);

  const toggleClient = (id) => setOpenClients((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  return (
    <div data-testid="copy-cost-contracts">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-sm">Contratti costo copia</h3>
          <p className="text-xs text-muted-foreground">Raggruppati per cliente.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente / contratto"
              className="rounded-sm h-9 pl-7 w-56" data-testid="cc-search" />
          </div>
          <Select value={statoF} onValueChange={setStatoF}>
            <SelectTrigger className="rounded-sm h-9 w-36" data-testid="cc-stato-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {["attivo", "da_rinnovare", "sospeso", "cessato"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="cc-kpi-cards">
        <button type="button" onClick={() => setRinnovoOnly((v) => !v)} data-testid="cc-kpi-rinnovo"
          className={`text-left rounded-sm border bg-white p-4 transition-colors ${rinnovoOnly ? "border-amber-500 bg-amber-50" : "hover:border-amber-300"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><CalendarBlank size={15} weight="fill" className="text-amber-500" /> Da rinnovare</div>
            <span className="text-2xl font-heading font-bold tabular-nums" data-testid="cc-kpi-rinnovo-count">{rinnovoCount}</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1.5">Contratti scaduti o da rinnovare{rinnovoOnly ? " · filtro attivo" : ""}</div>
        </button>
      </div>

      <div className="space-y-2" data-testid="cc-groups">
        {loading ? <div className="text-center text-muted-foreground py-8 text-sm">Caricamento…</div>
          : groups.length === 0 ? <div className="text-center text-muted-foreground py-8 text-sm border border-border rounded-sm">Nessun contratto costo copia</div>
            : groups.map((g) => {
              const isOpen = openClients.has(g.cliente_id);
              const pc = (coverage.per_client || {})[g.cliente_id] || { n_senza: 0 };
              return (
                <div key={g.cliente_id} className="border border-border rounded-sm overflow-hidden" data-testid={`cc-client-group-${g.cliente_id}`}>
                  <div className="w-full flex items-center gap-2 px-3 py-2.5 bg-secondary/40 hover:bg-secondary/70 transition-colors">
                    <button onClick={() => toggleClient(g.cliente_id)} data-testid={`cc-client-header-${g.cliente_id}`}
                      className="flex items-center gap-2 flex-1 text-left min-w-0">
                      {isOpen ? <CaretDown size={15} className="text-muted-foreground shrink-0" /> : <CaretRight size={15} className="text-muted-foreground shrink-0" />}
                      <Buildings size={16} className="text-primary shrink-0" />
                      <span className="font-medium text-sm truncate">{g.cliente_nome}</span>
                    </button>
                    <Badge variant="outline" className="rounded-sm text-[10px] gap-1"><FilePlus size={11} /> {g.contracts.length} contratt{g.contracts.length === 1 ? "o" : "i"}</Badge>
                    <Badge variant="outline" className="rounded-sm text-[10px] gap-1"><Printer size={11} /> {g.n_dev} fotocopiatric{g.n_dev === 1 ? "e" : "i"}</Badge>
                    {pc.n_senza > 0 && (
                      <button data-testid={`cc-client-uncovered-${g.cliente_id}`} title="Mostra le fotocopiatrici del cliente ancora da coprire"
                        onClick={(e) => { e.stopPropagation(); navigate(`/dispositivi?prt=senza_contratto&cliente=${g.cliente_id}`); }}
                        className="inline-flex items-center gap-1 rounded-sm border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors text-[10px] font-medium px-1.5 py-0.5">
                        <WarningCircle size={11} weight="fill" /> {pc.n_senza} senza contratto
                      </button>
                    )}
                  </div>
                  {isOpen && (
                    <div className="divide-y divide-border">
                      {g.contracts.map((c) => (
                        <ContractRow key={c.id} c={c} onOpen={() => setDetailId(c.id)} onEdit={() => navigate(`/contratti/${c.id}`)} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
      </div>

      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) setEditId(null); }}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader><DialogTitle>{editId ? "Modifica contratto costo copia" : "Nuovo contratto costo copia"}</DialogTitle>
            <DialogDescription>Riusa l'anagrafica contratti canonica (categoria Noleggio/Costo Copia).</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Cliente *</Label>
              <ClientCombobox clients={clients.map((c) => ({ id: c.id, ragione_sociale: c.nome }))}
                value={form.cliente_id} onChange={(v) => setForm((f) => ({ ...f, cliente_id: v }))}
                placeholder="Cerca cliente…" testid="cc-cliente-select" /></div>
            <div className="col-span-2"><Label className="text-xs">Titolo</Label><Input value={form.titolo} onChange={(e) => setForm((f) => ({ ...f, titolo: e.target.value }))} className="rounded-sm" data-testid="cc-titolo" placeholder="Es. Noleggio ufficio sede" /></div>
            <div><Label className="text-xs">Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setForm((f) => ({ ...f, stato: v }))}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["attivo", "da_rinnovare", "sospeso", "cessato"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Periodicità</Label>
              <Select value={form.periodicita} onValueChange={(v) => setForm((f) => ({ ...f, periodicita: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="cc-periodicita"><SelectValue /></SelectTrigger>
                <SelectContent>{["mensile", "trimestrale", "semestrale", "annuale", "personalizzato"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Modalità periodo</Label>
              <Select value={form.period_mode} onValueChange={(v) => setForm((f) => ({ ...f, period_mode: v }))}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="calendario">Calendario</SelectItem><SelectItem value="decorrenza">Dalla decorrenza</SelectItem></SelectContent>
              </Select></div>
            <div><Label className="text-xs">Data inizio</Label><DateField value={form.data_inizio} onChange={(v) => setForm((f) => ({ ...f, data_inizio: v }))} testid="cc-data-inizio" /></div>
            <div><Label className="text-xs">Data scadenza</Label><DateField value={form.data_scadenza} onChange={(v) => setForm((f) => ({ ...f, data_scadenza: v }))} testid="cc-data-scadenza" /></div>
            <div className="col-span-2 border-t border-border pt-2 flex items-center justify-between">
              <Label className="text-xs font-medium">Canone generale contratto</Label>
              <Switch checked={form.canone_generale.attivo} onCheckedChange={(v) => setForm((f) => ({ ...f, canone_generale: { ...f.canone_generale, attivo: v } }))} data-testid="cc-canone-gen-switch" />
            </div>
            {form.canone_generale.attivo && (
              <>
                <div><Label className="text-xs">Importo imponibile (€)</Label><Input type="number" step="0.01" value={form.canone_generale.importo} onChange={(e) => setForm((f) => ({ ...f, canone_generale: { ...f.canone_generale, importo: parseFloat(e.target.value) || 0 } }))} className="rounded-sm" data-testid="cc-canone-gen-importo" /></div>
                <div><Label className="text-xs">Periodo parziale canone gen.</Label>
                  <Select value={form.canone_generale.partial} onValueChange={(v) => setForm((f) => ({ ...f, canone_generale: { ...f.canone_generale, partial: v }, partial_canone_generale: v }))}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="intero">Intero</SelectItem><SelectItem value="prorata_giorni">Pro-rata giorni</SelectItem></SelectContent>
                  </Select></div>
              </>
            )}
            <div className="col-span-2 grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Parziale canone dispositivo</Label>
                <Select value={form.partial_canone_device} onValueChange={(v) => setForm((f) => ({ ...f, partial_canone_device: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="intero">Intero</SelectItem><SelectItem value="prorata_giorni">Pro-rata giorni</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-xs">Parziale copie incluse</Label>
                <Select value={form.partial_included} onValueChange={(v) => setForm((f) => ({ ...f, partial_included: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="intero">Intere</SelectItem><SelectItem value="prorata_giorni">Pro-rata giorni</SelectItem></SelectContent>
                </Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setCreateOpen(false); setEditId(null); }}>Annulla</Button>
            <Button className="rounded-sm" onClick={save} data-testid="cc-create-save-btn">{editId ? "Salva modifiche" : "Crea contratto"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scelta deep-link: nuovo vs esistente */}
      <Dialog open={choiceOpen} onOpenChange={setChoiceOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="cc-choice-dialog">
          <DialogHeader><DialogTitle>Associa fotocopiatrice a un contratto costo copia</DialogTitle>
            <DialogDescription>Scegli se collegare la fotocopiatrice a un contratto esistente del cliente oppure crearne uno nuovo.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-sm border border-border p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div>
                  <div className="font-medium text-sm">Associa a contratto esistente</div>
                  <div className="text-xs text-muted-foreground">Solo contratti costo copia validi di questo cliente.</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={existingSel} onValueChange={setExistingSel}>
                  <SelectTrigger className="rounded-sm flex-1" data-testid="cc-existing-select">
                    <SelectValue placeholder={existingForClient.length ? "Seleziona contratto" : "Nessun contratto valido"} />
                  </SelectTrigger>
                  <SelectContent>
                    {existingForClient.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.numero} · {c.titolo || "—"} · {c.stato} · {c.n_dispositivi} disp.</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="rounded-sm gap-1.5" onClick={chooseExisting} disabled={!existingForClient.length}
                  data-testid="cc-choice-existing-confirm"><LinkSimple size={14} /> Associa</Button>
              </div>
              {!existingForClient.length && <p className="text-[11px] text-amber-600 mt-1.5">Questo cliente non ha contratti costo copia validi: crea un nuovo contratto.</p>}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><div className="h-px bg-border flex-1" />oppure<div className="h-px bg-border flex-1" /></div>
            <Button variant="outline" className="rounded-sm w-full gap-2" onClick={chooseNew} data-testid="cc-choice-new">
              <FilePlus size={15} /> Crea nuovo contratto costo copia
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {detailId && <ContractSheet contractId={detailId} clients={clients} presetDeviceId={presetDeviceId}
        autoAddDevice={autoAddDevice}
        onClose={() => { setDetailId(null); setAutoAddDevice(false); load(); onDone?.(); }} />}
    </div>
  );
}

function ContractRow({ c, onOpen, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [devices, setDevices] = useState(null);
  const toggle = async (e) => {
    e.stopPropagation();
    const next = !expanded; setExpanded(next);
    if (next && devices === null) {
      try { const { data } = await api.get(`/copy-cost-contracts/${c.id}`); setDevices(data.devices || []); }
      catch { setDevices([]); }
    }
  };
  return (
    <div data-testid={`cc-row-${c.id}`}>
      <div className="flex items-center gap-2 px-3 py-2 hover:bg-secondary/30 cursor-pointer" onClick={onOpen}>
        <button onClick={toggle} className="p-0.5 text-muted-foreground hover:text-foreground" data-testid={`cc-row-expand-${c.id}`}>
          {expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
        </button>
        <span className="font-medium text-sm">{c.numero}</span>
        <span className="text-xs text-muted-foreground flex-1 truncate">{c.titolo || "—"}</span>
        <StatusBadge status={c.stato} />
        {c.config_incompleta && (
          <Badge variant="outline" className="rounded-sm text-[9px] gap-1 bg-amber-50 text-amber-700 border-amber-200"
            title="Configurazione costo copia incompleta: mancano le condizioni economiche" data-testid={`cc-config-incompleta-${c.id}`}>
            <WarningCircle size={10} weight="fill" /> Configurazione costo copia incompleta
          </Badge>
        )}
        <Button size="sm" className="rounded-sm gap-1 h-7" onClick={(e) => { e.stopPropagation(); onEdit(); }} data-testid={`cc-open-btn-${c.id}`}><MagnifyingGlass size={13} /> Apri contratto</Button>
      </div>
      {expanded && (
        <div className="bg-secondary/20 px-9 py-2 border-t border-border/60" data-testid={`cc-row-devices-${c.id}`}>
          {devices === null ? <div className="text-xs text-muted-foreground">Caricamento…</div>
            : devices.length === 0 ? <div className="text-xs text-muted-foreground">Nessuna fotocopiatrice collegata</div>
              : <div className="space-y-1">
                {devices.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-xs">
                    <Printer size={12} className="text-muted-foreground" />
                    <span className="font-medium">{d.device_marca} {d.device_modello}</span>
                    <span className="text-muted-foreground">{d.device_serial}</span>
                    <span className="text-muted-foreground">· {TIP_LABEL[d.tipologia] || d.tipologia}</span>
                    {!d.config_completa && <Badge variant="outline" className="rounded-sm text-[9px] bg-amber-50 text-amber-700 border-amber-200">da configurare</Badge>}
                    <Badge variant="outline" className="rounded-sm text-[9px]">{d.stato}</Badge>
                  </div>
                ))}
              </div>}
        </div>
      )}
    </div>
  );
}

const EMPTY_DEV = {
  device_id: "", tipologia: "canone_costo_copia", periodo_da: "", periodo_a: "", stato: "attivo",
  meter_mono: "", meter_colore: "", meter_extra: [],
  prezzo_mono: 0, prezzo_colore: 0, prezzo_eccedenza_mono: 0, prezzo_eccedenza_colore: 0,
  copie_incluse_mono: 0, copie_incluse_colore: 0, canone_dispositivo: 0, minimo_fatturabile: 0,
};

export function ContractSheet({ contractId, clients, presetDeviceId, autoAddDevice, onClose }) {
  const [c, setC] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [devForm, setDevForm] = useState(EMPTY_DEV);
  const [meterOpts, setMeterOpts] = useState({ options: [], suggested_mono: null, suggested_colore: null });
  const [candidates, setCandidates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [versions, setVersions] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const autoAddRef = useRef(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/copy-cost-contracts/${contractId}`);
    setC(data);
    const { data: v } = await api.get(`/copy-cost-contracts/${contractId}/versions`);
    setVersions(v || []);
  }, [contractId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!c) return;
    Promise.all([
      api.get(`/devices?cliente_id=${c.cliente_id}`),
      api.get(`/copy-cost-contracts/device-coverage`).catch(() => ({ data: {} })),
    ]).then(([{ data }, cov]) => {
      const covered = new Set((cov?.data?.covered_device_ids) || []);
      const list = (data?.items || data || [])
        .filter((d) => (d.source === "printanista" || d.printanista?.linked))
        .filter((d) => d.id === presetDeviceId || !covered.has(d.id));
      setCandidates(list);
    }).catch(() => setCandidates([]));
  }, [c, presetDeviceId]);

  const loadMeters = async (deviceId) => {
    if (!deviceId) return;
    try {
      const { data } = await api.get(`/devices/${deviceId}/billable-meter-options`);
      setMeterOpts(data);
      const opts = data?.options || [];
      const match = (re) => (opts.find((o) => re.test(o.label || "") || re.test(o.code || "")) || {}).code;
      const monoDef = match(/totale\s+pagine\s+mono/i) || data?.suggested_mono || "";
      const colDef = match(/totale\s+pagine\s+colore/i) || data?.suggested_colore || "";
      setDevForm((f) => ({
        ...f,
        meter_mono: f.meter_mono || monoDef,
        meter_colore: f.meter_colore || colDef,
      }));
    } catch { setMeterOpts({ options: [], suggested_mono: null, suggested_colore: null }); }
  };
  const openAdd = useCallback(() => {
    setDevForm({ ...EMPTY_DEV, device_id: presetDeviceId || "" });
    setMeterOpts({ options: [], suggested_mono: null, suggested_colore: null });
    setAddOpen(true);
    if (presetDeviceId) loadMeters(presetDeviceId);
  }, [presetDeviceId]);

  // deep-link "Associa a contratto esistente" / dopo la creazione: apri subito il collegamento
  useEffect(() => {
    if (autoAddDevice && presetDeviceId && c && !autoAddRef.current) {
      autoAddRef.current = true;
      openAdd();
    }
  }, [autoAddDevice, presetDeviceId, c, openAdd]);

  const addDevice = async () => {
    if (!devForm.device_id) { toast.error("Seleziona un dispositivo"); return; }
    try {
      await api.post(`/copy-cost-contracts/${contractId}/devices`, devForm);
      toast.success("Dispositivo collegato al contratto");
      setAddOpen(false); load();
    } catch (e) { toast.error(err(e)); }
  };
  const removeDevice = async (linkId, mode) => {
    try {
      await api.delete(`/copy-cost-contracts/${contractId}/devices/${linkId}?mode=${mode}`);
      toast.success(mode === "delete" ? "Collegamento eliminato definitivamente" : "Stampante archiviata (collegamento terminato)");
      setConfirmDel(null); load();
    } catch (e) { toast.error(err(e)); }
  };
  const runPreview = async (linkId) => {
    setPreview({ linkId, loading: true });
    try {
      const { data } = await api.get(`/copy-cost-contracts/${contractId}/devices/${linkId}/preview`);
      setPreview({ linkId, data });
    } catch (e) { toast.error(err(e)); setPreview(null); }
  };

  if (!c) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto" data-testid="cc-contract-sheet">
        <DialogHeader><DialogTitle>{c.numero ? `${c.numero} · ${c.titolo}` : c.titolo}</DialogTitle>
          <DialogDescription>{c.cliente_id} · stato <b>{c.stato_effettivo}</b> · {(c.copy_cost_cfg || {}).periodicita}</DialogDescription></DialogHeader>
        <Tabs defaultValue="dispositivi">
          <TabsList className="rounded-sm">
            <TabsTrigger value="dispositivi" data-testid="cc-tab-dispositivi">Dispositivi</TabsTrigger>
            <TabsTrigger value="versioni" data-testid="cc-tab-versioni">Storico versioni</TabsTrigger>
            <TabsTrigger value="rendicontazioni" data-testid="cc-tab-rendicontazioni">Rendicontazioni</TabsTrigger>
          </TabsList>
          <TabsContent value="dispositivi" className="pt-3 space-y-3">
            <div className="flex justify-end">
              <Button size="sm" className="rounded-sm gap-2" onClick={openAdd} data-testid="cc-add-device-btn"><LinkSimple size={14} /> Collega dispositivo</Button>
            </div>
            <div className="border border-border rounded-sm overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Dispositivo</TableHead><TableHead>Tipologia</TableHead><TableHead>Cont. mono</TableHead>
                  <TableHead>Cont. colore</TableHead><TableHead>Canone</TableHead><TableHead>Minimo</TableHead>
                  <TableHead>Stato</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(c.devices || []).length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-4">Nessun dispositivo collegato</TableCell></TableRow>
                    : c.devices.map((d) => (
                      <TableRow key={d.id} data-testid={`cc-dev-row-${d.id}`}>
                        <TableCell className="text-xs font-medium">{d.device_marca} {d.device_modello}<div className="text-[10px] text-muted-foreground">{d.device_serial}</div></TableCell>
                        <TableCell className="text-xs">{TIP_LABEL[d.tipologia]}</TableCell>
                        <TableCell className="text-xs">{d.meter_mono || <span className="text-amber-600">da config.</span>}</TableCell>
                        <TableCell className="text-xs">{d.meter_colore || "—"}</TableCell>
                        <TableCell className="text-xs">€ {d.canone_dispositivo}</TableCell>
                        <TableCell className="text-xs">€ {d.minimo_fatturabile}</TableCell>
                        <TableCell><Badge variant="outline" className="rounded-sm text-[10px]">{d.stato}</Badge></TableCell>
                        <TableCell className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => runPreview(d.id)} data-testid={`cc-preview-${d.id}`}><Calculator size={14} /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => setConfirmDel(d)} data-testid={`cc-remove-dev-${d.id}`}><Trash size={14} /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
            {preview?.data && (
              <div className="rounded-sm border border-blue-200 bg-blue-50/50 p-3 text-xs space-y-1" data-testid="cc-preview-box">
                <div className="font-medium flex items-center gap-1.5"><Calculator size={14} /> Anteprima {preview.data.period_start} → {preview.data.period_end} <span className="text-muted-foreground">(non vincolante)</span></div>
                <div>Canone dispositivo: € {preview.data.canone_dispositivo} · Copie mono fatturate: {preview.data.fatturate_mono} (€ {preview.data.componente_mono}) · colore: {preview.data.fatturate_colore} (€ {preview.data.componente_colore})</div>
                <div>Subtotale: € {preview.data.subtotale} · Minimo: € {preview.data.minimo_fatturabile} {preview.data.minimo_applicato && <b className="text-amber-700">(applicato, +€ {preview.data.differenza_minimo})</b>}</div>
                <div className="font-semibold">Totale dispositivo: € {preview.data.totale_dispositivo} · Canone generale contratto (a parte): € {preview.data.canone_generale_contratto}</div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="versioni" className="pt-3">
            <div className="border border-border rounded-sm divide-y divide-border max-h-[40vh] overflow-y-auto">
              {versions.length === 0 ? <div className="text-center text-muted-foreground py-4 text-sm">Nessuna versione</div>
                : versions.map((v) => (
                  <div key={v.id} className="p-2 text-xs flex items-center gap-2">
                    <ClockCounterClockwise size={14} className="text-muted-foreground" />
                    <span className="font-medium">{v.valid_from}</span>{v.valid_to && <span>→ {v.valid_to}</span>}
                    <span className="text-muted-foreground">· {v.reason} · {v.user}</span>
                  </div>
                ))}
            </div>
          </TabsContent>
          <TabsContent value="rendicontazioni" className="pt-3">
            <CopyCostStatements contractId={contractId} />
          </TabsContent>
        </Tabs>

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="cc-add-device-dialog">
            <DialogHeader><DialogTitle>Collega dispositivo al contratto</DialogTitle>
              <DialogDescription>Seleziona i contatori fatturabili reali. Le scansioni non sono selezionabili. Le copiatrici già coperte da un contratto attivo non compaiono in elenco.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Dispositivo (cliente del contratto)</Label>
                <Select value={devForm.device_id} onValueChange={(v) => { setDevForm((f) => ({ ...f, device_id: v })); loadMeters(v); }}>
                  <SelectTrigger className="rounded-sm" data-testid="cc-dev-select"><SelectValue placeholder="Seleziona fotocopiatrice" /></SelectTrigger>
                  <SelectContent>{candidates.map((d) => <SelectItem key={d.id} value={d.id}>{d.marca} {d.modello} · {d.serial}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Tipologia</Label>
                <Select value={devForm.tipologia} onValueChange={(v) => setDevForm((f) => ({ ...f, tipologia: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="cc-dev-tipologia"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TIP_LABEL).filter(([k]) => k !== "canone_incluse_eccedenze").map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Stato</Label>
                <Select value={devForm.stato} onValueChange={(v) => setDevForm((f) => ({ ...f, stato: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="attivo">Attivo</SelectItem><SelectItem value="dismesso">Dismesso</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-xs">Contatore mono fatturabile</Label>
                <Select value={devForm.meter_mono} onValueChange={(v) => setDevForm((f) => ({ ...f, meter_mono: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="cc-meter-mono"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{meterOpts.options.map((o) => <SelectItem key={o.code} value={o.code}>{o.label || o.code} · {o.categoria} · ult. {o.ultima_lettura}</SelectItem>)}</SelectContent>
                </Select>
                {meterOpts.suggested_mono && <div className="text-[10px] text-muted-foreground mt-0.5">Suggerito: {(meterOpts.options.find((o) => o.code === meterOpts.suggested_mono) || {}).label || meterOpts.suggested_mono} (conferma)</div>}
              </div>
              <div><Label className="text-xs">Contatore colore fatturabile</Label>
                <Select value={devForm.meter_colore} onValueChange={(v) => setDevForm((f) => ({ ...f, meter_colore: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="cc-meter-colore"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{meterOpts.options.map((o) => <SelectItem key={o.code} value={o.code}>{o.label || o.code} · {o.categoria} · ult. {o.ultima_lettura}</SelectItem>)}</SelectContent>
                </Select>
                {meterOpts.suggested_colore && <div className="text-[10px] text-muted-foreground mt-0.5">Suggerito: {(meterOpts.options.find((o) => o.code === meterOpts.suggested_colore) || {}).label || meterOpts.suggested_colore} (conferma)</div>}</div>
              <div><Label className="text-xs">Prezzo copia mono (€)</Label><Input type="number" step="0.000001" value={devForm.prezzo_mono} onChange={(e) => setDevForm((f) => ({ ...f, prezzo_mono: parseFloat(e.target.value) || 0 }))} className="rounded-sm" data-testid="cc-prezzo-mono" /></div>
              <div><Label className="text-xs">Prezzo copia colore (€)</Label><Input type="number" step="0.000001" value={devForm.prezzo_colore} onChange={(e) => setDevForm((f) => ({ ...f, prezzo_colore: parseFloat(e.target.value) || 0 }))} className="rounded-sm" /></div>
              <div><Label className="text-xs">Copie incluse mono</Label><Input type="number" value={devForm.copie_incluse_mono} onChange={(e) => setDevForm((f) => ({ ...f, copie_incluse_mono: parseFloat(e.target.value) || 0 }))} className="rounded-sm" /></div>
              <div><Label className="text-xs">Copie incluse colore</Label><Input type="number" value={devForm.copie_incluse_colore} onChange={(e) => setDevForm((f) => ({ ...f, copie_incluse_colore: parseFloat(e.target.value) || 0 }))} className="rounded-sm" /></div>
              <div><Label className="text-xs">Prezzo eccedenza mono (€)</Label><Input type="number" step="0.000001" value={devForm.prezzo_eccedenza_mono} onChange={(e) => setDevForm((f) => ({ ...f, prezzo_eccedenza_mono: parseFloat(e.target.value) || 0 }))} className="rounded-sm" /></div>
              <div><Label className="text-xs">Prezzo eccedenza colore (€)</Label><Input type="number" step="0.000001" value={devForm.prezzo_eccedenza_colore} onChange={(e) => setDevForm((f) => ({ ...f, prezzo_eccedenza_colore: parseFloat(e.target.value) || 0 }))} className="rounded-sm" /></div>
              <div><Label className="text-xs">Canone dispositivo (€)</Label><Input type="number" step="0.01" value={devForm.canone_dispositivo} onChange={(e) => setDevForm((f) => ({ ...f, canone_dispositivo: parseFloat(e.target.value) || 0 }))} className="rounded-sm" data-testid="cc-canone-dev" /></div>
              <div><Label className="text-xs">Minimo fatturabile (€)</Label><Input type="number" step="0.01" value={devForm.minimo_fatturabile} onChange={(e) => setDevForm((f) => ({ ...f, minimo_fatturabile: parseFloat(e.target.value) || 0 }))} className="rounded-sm" data-testid="cc-minimo" /></div>
            </div>
            <p className="text-[11px] text-muted-foreground">Il minimo fatturabile si applica a canone dispositivo + copie (il canone generale del contratto è escluso).</p>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setAddOpen(false)}>Annulla</Button>
              <Button className="rounded-sm" onClick={addDevice} data-testid="cc-add-device-save-btn">Collega</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
          <DialogContent className="sm:max-w-[440px]" data-testid="cc-remove-dev-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">Rimuovi dispositivo dal contratto</DialogTitle>
              <DialogDescription className="text-xs">
                {confirmDel ? <>{confirmDel.device_marca} {confirmDel.device_modello} · {confirmDel.device_serial}</> : null}
                <br />Scegli come procedere con questa stampante su questo contratto.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-xs">
              <div className="rounded-sm border border-border px-3 py-2">
                <div className="font-medium text-sm">Archivia stampante</div>
                <div className="text-muted-foreground">Termina il collegamento mantenendo lo storico (stato «dismesso»). Rendicontazioni e versioni restano consultabili.</div>
              </div>
              <div className="rounded-sm border border-destructive/30 px-3 py-2">
                <div className="font-medium text-sm text-destructive">Cancella definitivamente</div>
                <div className="text-muted-foreground">Elimina l'intera riga e tutti i collegamenti creati su questo contratto (condizioni economiche e rettifiche). Operazione irreversibile.</div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setConfirmDel(null)} data-testid="cc-remove-dev-cancel">Annulla</Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => removeDevice(confirmDel.id, "archive")} data-testid="cc-remove-dev-archive">Archivia stampante</Button>
              <Button variant="destructive" size="sm" className="rounded-sm" onClick={() => removeDevice(confirmDel.id, "delete")} data-testid="cc-remove-dev-delete">Cancella definitivamente</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
